import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Pencil,
  Play,
  Search,
  Trash2,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"
import { formatFullDate } from "@/lib/format"
import {
  useChaptersList,
  useDeleteChapter,
  type AdminChapter,
} from "@/lib/queries/chapters"
import { ExportButton } from "@/components/ExportButton"
import { api } from "@/lib/api"

// ───────────────────────────────────────────────────────────────────────
// PAGE: Chapters (list)
// LEGACY: apps/admin/src/pages/Chapter.jsx
// API:    POST /admin/chapter_list   (server pagination/sort/search)
//         POST /admin/chapter_delete (delete)
//
// ─── Field parity (legacy ↔ v2) ──────────────────────────────────────
// LIST request body:
//   page, limit, search, sortyBy?, sortDirection?    (sortyBy typo PRESERVED)
//
// LIST response columns (same set + same data as legacy):
//   cover_image  → "Cover" (img)
//   title        → "Title" (sortable)
//   author       → "Author" (sortable)
//   chapter_type → "Type"  (sortable; "1" → Free, else Paid)
//   video_url    → "Play" button → modal player
//   order        → "Order" (sortable)
//   created_at   → "Created" (sortable)
//   _id (action) → Update link → /chapters/:id (was /updateChapter/:id)
//   _id (action) → Delete button → POST /admin/chapter_delete {id}
//
// DELETE confirm payload (POST /admin/chapter_delete):
//   id           → id
// ───────────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const

function useDebounced<T>(value: T, delay = 300): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return v
}

function chapterTypeLabel(t: AdminChapter["chapter_type"]): string {
  return t === 1 || t === "1" ? "Free" : "Paid"
}

export default function Chapters() {
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebounced(search, 300)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState<number>(10)
  const [sortyBy, setSortyBy] = useState<string | undefined>(undefined)
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1)

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, limit, sortyBy, sortDirection])

  const params = useMemo(
    () => ({
      page,
      limit,
      search: debouncedSearch || undefined,
      sortyBy,
      sortDirection,
    }),
    [page, limit, debouncedSearch, sortyBy, sortDirection]
  )

  const chaptersQ = useChaptersList(params)
  const deleteMut = useDeleteChapter()

  const [playChapter, setPlayChapter] = useState<AdminChapter | null>(null)
  const [deleteChapter, setDeleteChapter] = useState<AdminChapter | null>(null)

  const onSortClick = (field: string) => {
    if (sortyBy === field) {
      setSortDirection((prev) => (prev === 1 ? -1 : 1))
    } else {
      setSortyBy(field)
      setSortDirection(-1)
    }
  }

  const SortIcon = ({ field }: { field: string }) => {
    if (sortyBy !== field) return null
    return sortDirection === 1 ? (
      <ArrowUp className="ml-1 inline h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 inline h-3 w-3" />
    )
  }

  const totalPages = chaptersQ.data?.totalPages ?? 0
  const totalDocs = chaptersQ.data?.totalDocs ?? 0

  const handleDeleteConfirm = async () => {
    if (!deleteChapter) return
    await deleteMut.mutateAsync(deleteChapter._id)
    setDeleteChapter(null)
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="gap-4 sm:flex-row sm:items-end sm:justify-between sm:space-y-0">
          <div>
            <CardTitle>Chapters</CardTitle>
            <CardDescription>
              {totalDocs > 0
                ? `${totalDocs.toLocaleString()} total`
                : "Manage chapters across albums"}
            </CardDescription>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <div className="relative w-full sm:max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search chapters…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                aria-label="Search chapters"
              />
            </div>
            <ExportButton<AdminChapter>
              filenamePrefix="chapters"
              totalEstimate={totalDocs}
              columns={[
                { label: "Title", get: (c) => c.title },
                { label: "Author", get: (c) => c.author },
                { label: "Type", get: (c) => chapterTypeLabel(c.chapter_type) },
                { label: "Order", get: (c) => c.order },
                { label: "Description", get: (c) => c.description },
                { label: "Duration (sec)", get: (c) => c.durationInSec ?? c.duration },
                { label: "Like Count", get: (c) => c.like_count ?? 0 },
                { label: "Share Count", get: (c) => c.share_count ?? 0 },
                { label: "Comment Count", get: (c) => c.comment_count ?? 0 },
                { label: "Cover Image", get: (c) => c.cover_image },
                { label: "Video URL", get: (c) => c.video_url },
                { label: "Created", get: (c) => c.created_at },
                { label: "ID", get: (c) => c._id },
              ]}
              request={async (page, limit) => {
                const res = await api.post("/admin/chapter_list", {
                  page,
                  limit,
                  search: debouncedSearch || undefined,
                  sortyBy,
                  sortDirection,
                })
                return {
                  docs: res.data?.data?.docs ?? [],
                  totalDocs: res.data?.data?.totalDocs ?? 0,
                }
              }}
            />
            <Button asChild>
              <Link to="/chapters/new">Add chapter</Link>
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardContent className="p-0">
          {chaptersQ.isError && (
            <div
              role="alert"
              className="m-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Failed to load chapters. Try refreshing.</span>
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Cover</TableHead>
                <SortableTH field="title" label="Title" {...{ onSortClick, SortIcon }} />
                <SortableTH field="author" label="Author" {...{ onSortClick, SortIcon }} />
                <SortableTH
                  field="chapter_type"
                  label="Type"
                  className="w-20"
                  {...{ onSortClick, SortIcon }}
                />
                <SortableTH
                  field="order"
                  label="Order"
                  className="w-20"
                  {...{ onSortClick, SortIcon }}
                />
                <SortableTH field="created_at" label="Created" {...{ onSortClick, SortIcon }} />
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {chaptersQ.isLoading && (
                <TableRow>
                  <TableCell colSpan={7} className="h-32 text-center text-muted-foreground">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </TableCell>
                </TableRow>
              )}
              {!chaptersQ.isLoading && (chaptersQ.data?.docs.length ?? 0) === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="h-32 text-center text-muted-foreground">
                    No chapters match your filters.
                  </TableCell>
                </TableRow>
              )}
              {chaptersQ.data?.docs.map((c) => (
                <TableRow key={c._id}>
                  <TableCell>
                    {c.cover_image ? (
                      <img
                        src={c.cover_image}
                        alt=""
                        loading="lazy"
                        className="h-16 w-16 rounded object-cover"
                      />
                    ) : (
                      <div className="grid h-16 w-16 place-items-center rounded bg-muted text-xs text-muted-foreground">
                        no img
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap font-medium">
                    {c.title || "—"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {c.author || "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {chapterTypeLabel(c.chapter_type)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {typeof c.order === "number" ? c.order : "—"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {formatFullDate(c.created_at)}
                  </TableCell>
                  <TableCell className="space-x-1 text-right">
                    {c.video_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPlayChapter(c)}
                        aria-label={`Play ${c.title || "chapter"}`}
                      >
                        <Play className="h-4 w-4" />
                      </Button>
                    )}
                    <Button asChild variant="ghost" size="sm" aria-label="Edit chapter">
                      <Link to={`/chapters/${c._id}`}>
                        <Pencil className="h-4 w-4" />
                      </Link>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteChapter(c)}
                      aria-label="Delete chapter"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>

        <div className="flex flex-col gap-3 border-t px-4 py-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <span>Rows per page</span>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="h-8 rounded-md border border-input bg-background px-2 text-sm"
              aria-label="Rows per page"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <span>
              Page {page}
              {totalPages > 0 ? ` of ${totalPages}` : ""}
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={totalPages > 0 && page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Player modal */}
      <Dialog
        open={!!playChapter}
        onOpenChange={(open) => !open && setPlayChapter(null)}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{playChapter?.title || "Chapter"}</DialogTitle>
            <DialogDescription>{playChapter?.description}</DialogDescription>
          </DialogHeader>
          {playChapter?.video_url ? (
            <video
              src={playChapter.video_url}
              controls
              autoPlay
              playsInline
              className="aspect-video w-full rounded bg-black"
            />
          ) : (
            <div className="grid h-48 place-items-center text-muted-foreground">
              Video URL missing.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlayChapter(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete modal */}
      <Dialog
        open={!!deleteChapter}
        onOpenChange={(open) => !open && setDeleteChapter(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete chapter?</DialogTitle>
            <DialogDescription>
              {deleteChapter?.title || "This chapter"} will be removed. This
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteChapter(null)}
              disabled={deleteMut.isPending}
            >
              Keep chapter
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteMut.isPending}
            >
              {deleteMut.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete chapter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function SortableTH({
  field,
  label,
  className,
  onSortClick,
  SortIcon,
}: {
  field: string
  label: React.ReactNode
  className?: string
  onSortClick: (field: string) => void
  SortIcon: React.ComponentType<{ field: string }>
}) {
  return (
    <TableHead className={cn("cursor-pointer select-none", className)}>
      <button
        type="button"
        onClick={() => onSortClick(field)}
        className="flex items-center gap-1 text-left font-medium text-muted-foreground hover:text-foreground"
      >
        {label}
        <SortIcon field={field} />
      </button>
    </TableHead>
  )
}
